import { useEffect, useSyncExternalStore } from "react"
import {
	initNotificationSound,
	playNotificationSound,
} from "../Notifications/sound"

/**
 * WhatsApp-inbox notifications: reuses the shared notification sound and adds a
 * tab-title unread badge shown while the tab is backgrounded — the universal
 * fallback for when the sound is muted or blocked (e.g. iOS).
 *
 * A module-level singleton (not React state) because both the inbox list and
 * the open thread feed it, and the listeners must be attached exactly once.
 */

// How often the tab title alternates between the unread badge and the base
// title, so a backgrounded tab visibly blinks for attention.
const TITLE_FLASH_INTERVAL_MS = 1000

// Re-exported so the inbox bell keeps a single import surface for mute control.
export { useNotificationSoundMute as useNotificationMute } from "../Notifications/sound"

/**
 * Prime the shared sound engine and attach the badge's visibility listener.
 * Idempotent — safe to call from every inbox render.
 */
export function initNotifications() {
	initNotificationSound()
	if (visibilityAttached) {
		return
	}
	visibilityAttached = true
	document.addEventListener("visibilitychange", () => {
		if (!document.hidden) {
			resetBadge()
		}
	})
}

// --- Unread badge (tab title) ----------------------------------------------

let visibilityAttached = false
const badgeListeners = new Set<() => void>()
let badgeCount = 0

export function getBadgeCount(): number {
	return badgeCount
}

function setBadgeCount(next: number) {
	if (next === badgeCount) {
		return
	}
	badgeCount = next
	badgeListeners.forEach((listener) => listener())
}

export function resetBadge() {
	setBadgeCount(0)
}

/**
 * Signal a new inbound message: play the shared ding and, if the tab is in the
 * background, bump the unread badge.
 */
export function notifyInbound() {
	playNotificationSound()
	if (document.hidden) {
		setBadgeCount(badgeCount + 1)
	}
}

// Reactive unread-badge count.
function useNotificationBadge(): number {
	return useSyncExternalStore(
		(listener) => {
			badgeListeners.add(listener)
			return () => badgeListeners.delete(listener)
		},
		getBadgeCount,
		getBadgeCount
	)
}

/**
 * While there are unread messages, blink the tab title every second between
 * `(N Messages) <title>` and the plain `<title>` to draw attention.
 *
 * The title is written to `document.title` DIRECTLY rather than through Helmet:
 * Helmet applies title changes inside `requestAnimationFrame`, which browsers
 * pause for backgrounded tabs — exactly when this blink needs to run. A direct
 * assignment is synchronous and fires in the background. The steady title stays
 * with Helmet; this only takes over while unread, and restores the base (the
 * Helmet-rendered title, template included) when it stops.
 */
export function useUnreadTitleFlash() {
	const count = useNotificationBadge()

	useEffect(() => {
		if (count <= 0) {
			return
		}
		// Capture the current (Helmet-set) title as the base to flash against and
		// restore to — it already includes the app-title template suffix.
		const base = document.title
		const noun = count === 1 ? "Message" : "Messages"
		const badged = `(${count} ${noun}) ${base}`
		let showBadge = true
		const render = () => {
			document.title = showBadge ? badged : base
		}
		render()
		const id = window.setInterval(() => {
			showBadge = !showBadge
			render()
		}, TITLE_FLASH_INTERVAL_MS)
		return () => {
			window.clearInterval(id)
			document.title = base
		}
	}, [count])
}
